import {IFilter} from '../../../../../../_metronic/helpers'
import {normalizeErrorReasonKeys} from './invoiceErrorReasons'

const INVOICE_LIST_PATH = '/apps/invoice-management/invoices'
// «صورتحساب‌های آرشیو» (InvoicesPage, baseMode 3).
const INVOICE_ARCHIVE_PATH = '/apps/invoice-management/archives'
const INVOICE_LIST_TARGET_PARAMETER = 'invoiceId'
// A free-text search the list opens with (an alert SMS's tax id), across every season.
const INVOICE_LIST_SEARCH_PARAMETER = 'search'
const MAX_SEARCH_LENGTH = 100

// The query string the dashboard and the reports link with: a send status, a registration or an
// invoice day range, the registration channels, a goods/services search and the invoice's type,
// pattern, subject, buyer type and settlement. Each value is checked here, so a hand-edited
// address can never put an unknown value into the list's filter.
const SEND_STATUS_PARAMETER = 'sendStatus'
const SAVE_FROM_PARAMETER = 'saveFrom'
const SAVE_TO_PARAMETER = 'saveTo'
const FROM_PARAMETER = 'from'
const TO_PARAMETER = 'to'
const ENTRY_TYPE_PARAMETER = 'entryType'
const PRODUCT_PARAMETER = 'product'
const TYPE_PARAMETER = 'type'
const TEMPLATE_PARAMETER = 'template'
const SUBJECT_PARAMETER = 'subject'
const BUYER_TYPE_PARAMETER = 'buyerType'
const SETTLEMENT_PARAMETER = 'settlement'
// The invoices dashboard figures count: no cancellation invoice, nothing cancelled.
const COUNTED_PARAMETER = 'counted'
// «نوع خطا»: the keys of the reports' «علت خطاها» rows (code:0300101, text:<hash>), comma-separated.
const ERROR_REASON_PARAMETER = 'errorReason'

const SEND_STATUS_KEYS = ['notsend', 'errored', 'pending', 'unknown', 'success', 'canceled']
const JALALI_DAY = /^1[34]\d{2}\/(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])$/
const MAX_PRODUCT_SEARCH_LENGTH = 100
// The values the list's own filter offers for the invoice's type, pattern, subject, buyer type and
// settlement (InvoicesController filters each by equality).
const TYPE_VALUES = /^[1-3]$/
const TEMPLATE_VALUES = /^([1-9]|1[0-4])$/
const SUBJECT_VALUES = /^[1-4]$/
const BUYER_TYPE_VALUES = /^[1-4]$/
const SETTLEMENT_VALUES = /^[1-3]$/

export type InvoiceListFilterTarget = {
  sendStatus?: string
  saveFromDate?: string
  saveToDate?: string
  /** Invoice date, Jalali, both days included. */
  fromDate?: string
  toDate?: string
  entryTypes?: number[]
  productSearch?: string
  type?: number
  template?: number
  subject?: number
  buyerType?: number
  settlement?: number
  counted?: boolean
  /** «نوع خطا» keys; the list then shows failed invoices only. */
  errorReasons?: string[]
}

const readQuery = (search?: string) =>
  new URLSearchParams(
    search === undefined && typeof window !== 'undefined' ? window.location.search : search || ''
  )

export const getInvoiceListTargetInvoiceId = (search?: string): string => {
  const value = readQuery(search).get(INVOICE_LIST_TARGET_PARAMETER)?.trim() || ''

  if (!/^[1-9]\d*$/.test(value)) {
    return ''
  }

  const invoiceId = Number(value)
  return Number.isSafeInteger(invoiceId) ? invoiceId.toString() : ''
}

/** The search an address opens the list with, or '' when it carries none. */
export const getInvoiceListTargetSearch = (search?: string): string =>
  (readQuery(search).get(INVOICE_LIST_SEARCH_PARAMETER) || '').trim().slice(0, MAX_SEARCH_LENGTH)

export const buildInvoiceListSearchUrl = (text: string): string =>
  `${INVOICE_LIST_PATH}?${INVOICE_LIST_SEARCH_PARAMETER}=${encodeURIComponent(text.trim().slice(0, MAX_SEARCH_LENGTH))}`

/** One invoice in the sales list, or in the archive when it is archived. */
export const buildInvoiceListTargetUrl = (
  invoiceId: number,
  options?: {archived?: boolean}
): string =>
  `${
    options?.archived ? INVOICE_ARCHIVE_PATH : INVOICE_LIST_PATH
  }?${INVOICE_LIST_TARGET_PARAMETER}=${encodeURIComponent(invoiceId.toString())}`

/**
 * The list an address opens (InvoicesPage): 3 the archive, 2 purchases, otherwise sales. A link's
 * search starts in that list, so switching to it does not reset the season the search reads.
 */
export const getInvoiceListBaseMode = (pathname?: string): number => {
  const path = (
    pathname === undefined && typeof window !== 'undefined'
      ? window.location.pathname
      : pathname || ''
  ).replace(/\/+$/, '')
  if (path.endsWith('/invoice-management/archives')) return 3
  if (path.endsWith('/invoice-management/purchases')) return 2
  return 1
}

/** The list filter a dashboard or report link carries, or undefined when the address carries none. */
export const getInvoiceListTargetFilter = (search?: string): IFilter | undefined => {
  const query = readQuery(search)
  const filter: IFilter = {}

  const sendStatus = query.get(SEND_STATUS_PARAMETER)?.trim().toLowerCase() || ''
  if (SEND_STATUS_KEYS.includes(sendStatus)) {
    filter.sendStatus = [sendStatus]
  }

  const readDay = (parameter: string) => {
    const value = query.get(parameter)?.trim() || ''
    return JALALI_DAY.test(value) ? value : ''
  }

  const saveFrom = readDay(SAVE_FROM_PARAMETER)
  if (saveFrom) filter.saveFromDate = saveFrom
  const saveTo = readDay(SAVE_TO_PARAMETER)
  if (saveTo) filter.saveToDate = saveTo
  const from = readDay(FROM_PARAMETER)
  if (from) filter.fromDate = from
  const to = readDay(TO_PARAMETER)
  if (to) filter.toDate = to

  const entryTypes = (query.get(ENTRY_TYPE_PARAMETER) || '')
    .split(',')
    .map((value) => value.trim())
    .filter((value) => /^\d{1,3}$/.test(value) && Number(value) <= 255)
  if (entryTypes.length) {
    filter.entryTypes = Array.from(new Set(entryTypes))
  }

  const product = (query.get(PRODUCT_PARAMETER) || '').trim().slice(0, MAX_PRODUCT_SEARCH_LENGTH)
  if (product) {
    filter.productSearch = product
  }

  const readValue = (parameter: string, allowed: RegExp) => {
    const value = query.get(parameter)?.trim() || ''
    return allowed.test(value) ? value : ''
  }

  const type = readValue(TYPE_PARAMETER, TYPE_VALUES)
  if (type) filter.type = type
  const template = readValue(TEMPLATE_PARAMETER, TEMPLATE_VALUES)
  if (template) filter.template = template
  const subject = readValue(SUBJECT_PARAMETER, SUBJECT_VALUES)
  if (subject) filter.subject = subject
  const buyerType = readValue(BUYER_TYPE_PARAMETER, BUYER_TYPE_VALUES)
  if (buyerType) filter.buyerType = buyerType
  const settlement = readValue(SETTLEMENT_PARAMETER, SETTLEMENT_VALUES)
  if (settlement) filter.settlement = settlement

  if (query.get(COUNTED_PARAMETER) === '1') {
    filter.counted = '1'
  }

  const errorReasons = normalizeErrorReasonKeys((query.get(ERROR_REASON_PARAMETER) || '').split(','))
  if (errorReasons.length) {
    filter.errorReasons = errorReasons
    // An error is only on a failed invoice.
    filter.sendStatus = ['errored']
  }

  return Object.keys(filter).length ? filter : undefined
}

export const buildInvoiceListFilterUrl = (target: InvoiceListFilterTarget): string => {
  const query = new URLSearchParams()
  if (target.sendStatus) query.set(SEND_STATUS_PARAMETER, target.sendStatus)
  if (target.saveFromDate) query.set(SAVE_FROM_PARAMETER, target.saveFromDate)
  if (target.saveToDate) query.set(SAVE_TO_PARAMETER, target.saveToDate)
  if (target.fromDate) query.set(FROM_PARAMETER, target.fromDate)
  if (target.toDate) query.set(TO_PARAMETER, target.toDate)
  if (target.entryTypes?.length) query.set(ENTRY_TYPE_PARAMETER, target.entryTypes.join(','))
  if (target.productSearch) query.set(PRODUCT_PARAMETER, target.productSearch)
  if (target.type) query.set(TYPE_PARAMETER, String(target.type))
  if (target.template) query.set(TEMPLATE_PARAMETER, String(target.template))
  if (target.subject) query.set(SUBJECT_PARAMETER, String(target.subject))
  if (target.buyerType) query.set(BUYER_TYPE_PARAMETER, String(target.buyerType))
  if (target.settlement) query.set(SETTLEMENT_PARAMETER, String(target.settlement))
  if (target.counted) query.set(COUNTED_PARAMETER, '1')
  const errorReasons = normalizeErrorReasonKeys(target.errorReasons)
  if (errorReasons.length) query.set(ERROR_REASON_PARAMETER, errorReasons.join(','))

  const value = query.toString()
  return value ? `${INVOICE_LIST_PATH}?${value}` : INVOICE_LIST_PATH
}
